/**
 * Tic-Tac-Logic Solver
 *
 * Uses constraint propagation techniques followed by backtracking if needed.
 *
 * Techniques (in order of complexity):
 * 1. Block Adjacent Pairs: XX? -> XXO
 * 2. Block Sandwich: X?X -> XOX
 * 3. Complete by Count: Row has N/2 X's -> fill rest with O
 * 4. Anticipate Triples: Look ahead for forced triples
 * 5. Avoid Duplicates: Compare with complete rows/columns
 */

import {
  type CellValue,
  type SolverResult,
  type SolverTechnique,
  type HintResult,
  EMPTY,
  X,
  O,
  opposite,
} from './types';
import {
  getCell,
  setCell,
  getRow,
  getColumn,
  cloneCells,
  countSymbols,
  findEmptyCells,
  isLineComplete,
  linesIdentical,
} from './grid';
import { hasTripleViolation, hasCountViolation, hasDuplicateViolation, isSolved } from './validation';

/**
 * Solver options
 */
interface SolverOptions {
  useAdvanced?: boolean;
  allowBacktracking?: boolean;
  maxSolutions?: number;
}

/**
 * Apply "Block Adjacent Pairs" technique
 * If XX exists, place O on either side
 */
function blockAdjacentPairs(cells: CellValue[], size: number): boolean {
  let changed = false;

  // Check rows
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size - 1; x++) {
      const curr = getCell(cells, size, x, y);
      const next = getCell(cells, size, x + 1, y);

      if (curr !== EMPTY && curr === next) {
        // Block on the left
        if (x > 0 && getCell(cells, size, x - 1, y) === EMPTY) {
          setCell(cells, size, x - 1, y, opposite(curr));
          changed = true;
        }
        // Block on the right
        if (x + 2 < size && getCell(cells, size, x + 2, y) === EMPTY) {
          setCell(cells, size, x + 2, y, opposite(curr));
          changed = true;
        }
      }
    }
  }

  // Check columns
  for (let x = 0; x < size; x++) {
    for (let y = 0; y < size - 1; y++) {
      const curr = getCell(cells, size, x, y);
      const next = getCell(cells, size, x, y + 1);

      if (curr !== EMPTY && curr === next) {
        // Block above
        if (y > 0 && getCell(cells, size, x, y - 1) === EMPTY) {
          setCell(cells, size, x, y - 1, opposite(curr));
          changed = true;
        }
        // Block below
        if (y + 2 < size && getCell(cells, size, x, y + 2) === EMPTY) {
          setCell(cells, size, x, y + 2, opposite(curr));
          changed = true;
        }
      }
    }
  }

  return changed;
}

/**
 * Apply "Block Sandwich" technique
 * If X?X exists, place O in the middle
 */
function blockSandwich(cells: CellValue[], size: number): boolean {
  let changed = false;

  // Check rows
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size - 2; x++) {
      const first = getCell(cells, size, x, y);
      const middle = getCell(cells, size, x + 1, y);
      const last = getCell(cells, size, x + 2, y);

      if (first !== EMPTY && first === last && middle === EMPTY) {
        setCell(cells, size, x + 1, y, opposite(first));
        changed = true;
      }
    }
  }

  // Check columns
  for (let x = 0; x < size; x++) {
    for (let y = 0; y < size - 2; y++) {
      const first = getCell(cells, size, x, y);
      const middle = getCell(cells, size, x, y + 1);
      const last = getCell(cells, size, x, y + 2);

      if (first !== EMPTY && first === last && middle === EMPTY) {
        setCell(cells, size, x, y + 1, opposite(first));
        changed = true;
      }
    }
  }

  return changed;
}

/**
 * Apply "Complete by Count" technique
 * If a row/column has N/2 of one symbol, fill rest with opposite
 */
function completeByCount(cells: CellValue[], size: number): boolean {
  let changed = false;
  const maxPerLine = size / 2;

  // Check rows
  for (let y = 0; y < size; y++) {
    const row = getRow(cells, size, y);
    const counts = countSymbols(row);

    if (counts.x === maxPerLine && counts.empty > 0) {
      for (let x = 0; x < size; x++) {
        if (getCell(cells, size, x, y) === EMPTY) {
          setCell(cells, size, x, y, O);
          changed = true;
        }
      }
    } else if (counts.o === maxPerLine && counts.empty > 0) {
      for (let x = 0; x < size; x++) {
        if (getCell(cells, size, x, y) === EMPTY) {
          setCell(cells, size, x, y, X);
          changed = true;
        }
      }
    }
  }

  // Check columns
  for (let x = 0; x < size; x++) {
    const col = getColumn(cells, size, x);
    const counts = countSymbols(col);

    if (counts.x === maxPerLine && counts.empty > 0) {
      for (let y = 0; y < size; y++) {
        if (getCell(cells, size, x, y) === EMPTY) {
          setCell(cells, size, x, y, O);
          changed = true;
        }
      }
    } else if (counts.o === maxPerLine && counts.empty > 0) {
      for (let y = 0; y < size; y++) {
        if (getCell(cells, size, x, y) === EMPTY) {
          setCell(cells, size, x, y, X);
          changed = true;
        }
      }
    }
  }

  return changed;
}

/**
 * Apply "Anticipate Triples" technique
 * If placing one symbol would force a triple, place the other
 */
function anticipateTriples(cells: CellValue[], size: number): boolean {
  let changed = false;

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (getCell(cells, size, x, y) !== EMPTY) continue;

      let xForces = false;
      let oForces = false;

      // Try placing X
      const tryX = cloneCells(cells);
      setCell(tryX, size, x, y, X);
      applySimplePropagation(tryX, size);
      if (hasTripleViolation(tryX, size) || hasCountViolation(tryX, size)) {
        xForces = true;
      }

      // Try placing O
      const tryO = cloneCells(cells);
      setCell(tryO, size, x, y, O);
      applySimplePropagation(tryO, size);
      if (hasTripleViolation(tryO, size) || hasCountViolation(tryO, size)) {
        oForces = true;
      }

      // If one forces invalid state, use the other
      if (xForces && !oForces) {
        setCell(cells, size, x, y, O);
        changed = true;
      } else if (oForces && !xForces) {
        setCell(cells, size, x, y, X);
        changed = true;
      }
    }
  }

  return changed;
}

/**
 * Apply "Avoid Duplicates" technique
 * If a row/column is nearly complete, check if one option would create a duplicate
 */
function avoidDuplicates(cells: CellValue[], size: number): boolean {
  let changed = false;

  // Check rows with 1 or 2 empty cells
  for (let y = 0; y < size; y++) {
    const row = getRow(cells, size, y);
    const counts = countSymbols(row);

    if (counts.empty !== 1 && counts.empty !== 2) continue;

    // Find complete rows to compare against
    for (let otherY = 0; otherY < size; otherY++) {
      if (otherY === y) continue;
      const otherRow = getRow(cells, size, otherY);
      if (!isLineComplete(otherRow)) continue;

      // Check if filling one way would create duplicate
      const emptyPositions: number[] = [];
      for (let x = 0; x < size; x++) {
        if (row[x] === EMPTY) emptyPositions.push(x);
      }

      if (emptyPositions.length === 1) {
        const x = emptyPositions[0]!;
        // Try X
        const tryX = cloneCells(cells);
        setCell(tryX, size, x, y, X);
        const tryXRow = getRow(tryX, size, y);
        if (linesIdentical(tryXRow, otherRow)) {
          setCell(cells, size, x, y, O);
          changed = true;
        }
        // Try O
        const tryO = cloneCells(cells);
        setCell(tryO, size, x, y, O);
        const tryORow = getRow(tryO, size, y);
        if (linesIdentical(tryORow, otherRow)) {
          setCell(cells, size, x, y, X);
          changed = true;
        }
      }
    }
  }

  // Check columns with 1 or 2 empty cells
  for (let x = 0; x < size; x++) {
    const col = getColumn(cells, size, x);
    const counts = countSymbols(col);

    if (counts.empty !== 1 && counts.empty !== 2) continue;

    for (let otherX = 0; otherX < size; otherX++) {
      if (otherX === x) continue;
      const otherCol = getColumn(cells, size, otherX);
      if (!isLineComplete(otherCol)) continue;

      const emptyPositions: number[] = [];
      for (let y = 0; y < size; y++) {
        if (col[y] === EMPTY) emptyPositions.push(y);
      }

      if (emptyPositions.length === 1) {
        const y = emptyPositions[0]!;
        // Try X
        const tryX = cloneCells(cells);
        setCell(tryX, size, x, y, X);
        const tryXCol = getColumn(tryX, size, x);
        if (linesIdentical(tryXCol, otherCol)) {
          setCell(cells, size, x, y, O);
          changed = true;
        }
        // Try O
        const tryO = cloneCells(cells);
        setCell(tryO, size, x, y, O);
        const tryOCol = getColumn(tryO, size, x);
        if (linesIdentical(tryOCol, otherCol)) {
          setCell(cells, size, x, y, X);
          changed = true;
        }
      }
    }
  }

  return changed;
}

/**
 * Apply simple propagation (block pairs, sandwich, count)
 */
function applySimplePropagation(cells: CellValue[], size: number): boolean {
  let changed = true;
  let anyChange = false;

  while (changed) {
    changed = false;
    if (blockAdjacentPairs(cells, size)) changed = true;
    if (blockSandwich(cells, size)) changed = true;
    if (completeByCount(cells, size)) changed = true;
    if (changed) anyChange = true;
  }

  return anyChange;
}

/**
 * Check if grid has any violation
 */
function hasViolation(cells: CellValue[], size: number): boolean {
  return hasTripleViolation(cells, size) || hasCountViolation(cells, size) || hasDuplicateViolation(cells, size);
}

/**
 * Solve with backtracking (with iteration limit)
 */
function solveWithBacktracking(
  cells: CellValue[],
  size: number,
  maxSolutions: number,
  maxIterations: number = 50000
): { solutions: CellValue[][]; stopped: boolean; timedOut: boolean } {
  const solutions: CellValue[][] = [];
  let stopped = false;
  let iterations = 0;
  let timedOut = false;

  function backtrack(currentCells: CellValue[]): boolean {
    iterations++;

    // Check iteration limit
    if (iterations > maxIterations) {
      timedOut = true;
      return true; // Stop searching
    }

    // Apply propagation
    applySimplePropagation(currentCells, size);

    // Check for violation
    if (hasViolation(currentCells, size)) {
      return false;
    }

    // Check if solved
    if (isSolved(currentCells, size)) {
      solutions.push(cloneCells(currentCells));
      if (solutions.length >= maxSolutions) {
        stopped = true;
        return true;
      }
      return false; // Continue to find more solutions
    }

    // Find first empty cell
    const emptyCells = findEmptyCells(currentCells, size);
    if (emptyCells.length === 0) {
      return false;
    }

    // Use most constrained variable heuristic (cell with fewest valid options)
    const { x, y } = emptyCells[0]!;

    // Try X first
    const tryX = cloneCells(currentCells);
    setCell(tryX, size, x, y, X);
    if (backtrack(tryX)) {
      if (stopped || timedOut) return true;
    }

    // Try O
    const tryO = cloneCells(currentCells);
    setCell(tryO, size, x, y, O);
    if (backtrack(tryO)) {
      if (stopped || timedOut) return true;
    }

    return solutions.length > 0;
  }

  backtrack(cloneCells(cells));

  return { solutions, stopped, timedOut };
}

/**
 * Main solve function
 */
export function solve(cells: CellValue[], size: number, options: SolverOptions = {}): SolverResult {
  const { useAdvanced = true, allowBacktracking = true, maxSolutions = 2 } = options;

  const workingCells = cloneCells(cells);
  const techniquesUsed: Set<SolverTechnique> = new Set();

  let iterations = 0;
  const maxIterations = size * size * 10;

  while (iterations < maxIterations) {
    iterations++;
    let progress = false;

    // Apply basic techniques
    if (blockAdjacentPairs(workingCells, size)) {
      progress = true;
      techniquesUsed.add('blockAdjacentPairs');
    }

    if (blockSandwich(workingCells, size)) {
      progress = true;
      techniquesUsed.add('blockSandwich');
    }

    if (completeByCount(workingCells, size)) {
      progress = true;
      techniquesUsed.add('completeByCount');
    }

    // Check for violations
    if (hasViolation(workingCells, size)) {
      return { status: 'no_solution', techniquesUsed: [...techniquesUsed] };
    }

    // Check if solved
    if (isSolved(workingCells, size)) {
      return {
        status: 'solved',
        solution: workingCells,
        solutionCount: 1,
        techniquesUsed: [...techniquesUsed],
      };
    }

    // Try advanced techniques if no progress
    if (!progress && useAdvanced) {
      if (anticipateTriples(workingCells, size)) {
        progress = true;
        techniquesUsed.add('anticipateTriples');
      }

      if (!progress && avoidDuplicates(workingCells, size)) {
        progress = true;
        techniquesUsed.add('avoidDuplicates');
      }
    }

    if (!progress) {
      break;
    }
  }

  // If stuck and backtracking allowed, use it
  if (allowBacktracking) {
    techniquesUsed.add('backtracking');
    const { solutions, stopped, timedOut } = solveWithBacktracking(workingCells, size, maxSolutions);

    if (timedOut) {
      return { status: 'stuck', techniquesUsed: [...techniquesUsed] };
    }

    if (solutions.length === 0) {
      return { status: 'no_solution', techniquesUsed: [...techniquesUsed] };
    } else if (solutions.length === 1 && !stopped) {
      return {
        status: 'solved',
        solution: solutions[0],
        solutionCount: 1,
        techniquesUsed: [...techniquesUsed],
      };
    } else {
      return {
        status: 'multiple_solutions',
        solutionCount: solutions.length,
        techniquesUsed: [...techniquesUsed],
      };
    }
  }

  return { status: 'stuck', techniquesUsed: [...techniquesUsed] };
}

/**
 * Count solutions (for puzzle validation)
 * Uses limited iterations to avoid hanging on hard puzzles
 */
export function countSolutions(
  cells: CellValue[],
  size: number,
  maxCount: number = 2,
  maxIterations: number = 10000
): number {
  const { solutions, timedOut } = solveWithBacktracking(cells, size, maxCount, maxIterations);

  // If we timed out, return a high number to indicate uncertainty
  // This helps the generator know to keep this cell
  if (timedOut) {
    return maxCount + 1;
  }

  return solutions.length;
}

/**
 * Get a hint for the current puzzle state
 */
export function getHint(
  cells: CellValue[],
  solution: CellValue[],
  size: number
): HintResult | null {
  // Find first empty cell where solution differs
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const idx = y * size + x;
      if (cells[idx] === EMPTY && solution[idx] !== EMPTY) {
        return {
          x,
          y,
          value: solution[idx]!,
          technique: 'hint',
          explanation: `Place ${solution[idx] === X ? 'X' : 'O'} here`,
        };
      }
    }
  }
  return null;
}

/**
 * Solve puzzle and return solution
 */
export function getSolution(cells: CellValue[], size: number): CellValue[] | null {
  const result = solve(cells, size, { allowBacktracking: true, maxSolutions: 1 });
  return result.solution ?? null;
}
